/**
 * CompliancePill — Header indicator for shipment compliance status.
 *
 * Visually summarises the result of `useLiquidationGuard` in a compact
 * pill placed adjacent to the "Liquidar" button. Click to expand a
 * popover with the full checklist of applicable rules.
 *
 * States:
 *  🟢 Verde  — all critical rules satisfied & no warnings.
 *  🟡 Ámbar  — warnings present but no critical blockers.
 *  🔴 Rojo   — at least one critical blocker.
 */
import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, ShieldAlert, ShieldX, Check, X, AlertTriangle, FileWarning, GitCompareArrows, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LiquidationGuardResult } from "@/hooks/useLiquidationGuard";

interface Props {
  guard: LiquidationGuardResult;
  /** True when the shipment has already been liquidated (frozen). */
  isLiquidado?: boolean;
  /** Deep-link handler: opens the AuditPanel focused on a given document. */
  onOpenDocument?: (documentId: string) => void;
}

export function CompliancePill({ guard, isLiquidado, onOpenDocument }: Props) {
  const [open, setOpen] = useState(false);

  if (isLiquidado) {
    return (
      <Badge variant="outline" className="h-7 gap-1.5 border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5">
        <ShieldCheck className="h-3.5 w-3.5" />
        <span className="text-[11px] font-semibold">Liquidado</span>
      </Badge>
    );
  }

  const { summary, blockers, complianceChecklist } = guard;
  const tone =
    summary.missingCritical > 0 ? "critical" : summary.missingWarning > 0 ? "warning" : "ok";

  const toneStyles = {
    ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15",
    critical: "border-destructive/50 bg-destructive/10 text-destructive hover:bg-destructive/15",
  } as const;

  const Icon = tone === "ok" ? ShieldCheck : tone === "warning" ? ShieldAlert : ShieldX;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-7 gap-1.5 px-2.5 transition-colors tabular-nums",
            toneStyles[tone],
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="text-[11px] font-semibold">
            Compliance {summary.fulfilled}/{summary.totalRules}
          </span>
          {summary.missingCritical > 0 && (
            <span className="text-[10px] font-bold ml-0.5">· {summary.missingCritical} crítico{summary.missingCritical > 1 ? "s" : ""}</span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon className={cn(
              "h-4 w-4",
              tone === "ok" && "text-emerald-600 dark:text-emerald-400",
              tone === "warning" && "text-amber-600 dark:text-amber-400",
              tone === "critical" && "text-destructive",
            )} />
            <h4 className="text-sm font-semibold">Compliance Aduanero</h4>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
            Validación de documentación obligatoria antes de liquidar el embarque.
          </p>
        </div>

        {/* Checklist */}
        <div
          className="px-2 py-2 max-h-[280px] overflow-y-auto overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {complianceChecklist.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">
              No hay reglas de compliance aplicables a este embarque.
            </p>
          ) : (
            complianceChecklist.map(({ rule, present }) => (
              <div
                key={rule.id}
                className={cn(
                  "flex items-start gap-2 px-2 py-1.5 rounded-md text-[12px]",
                  !present && rule.severity === "critical" && "bg-destructive/5",
                  !present && rule.severity === "warning" && "bg-amber-500/5",
                )}
              >
                {present ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                ) : (
                  <X
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 mt-0.5",
                      rule.severity === "critical" ? "text-destructive" : "text-amber-600 dark:text-amber-400",
                    )}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "font-medium",
                        present
                          ? "text-foreground line-through opacity-60"
                          : rule.severity === "critical"
                            ? "text-destructive"
                            : "text-amber-700 dark:text-amber-400",
                      )}
                    >
                      {rule.label}
                    </span>
                    {!present && rule.severity === "warning" && (
                      <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/40 text-amber-700 dark:text-amber-400">
                        Opcional
                      </Badge>
                    )}
                    {!present && rule.severity === "critical" && (
                      <Badge variant="destructive" className="text-[9px] h-4 px-1">
                        Bloquea
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10.5px] text-muted-foreground leading-snug mt-0.5">
                    {rule.descripcion}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* HS Code blockers — granular per document with deep-link to AuditPanel */}
        {(() => {
          const hsBlockers = blockers.filter(
            (b) =>
              b.id.startsWith("hs-ia-pending::") ||
              b.id.startsWith("hs-mismatch::") ||
              b.id.startsWith("hs-format::"),
          );
          if (hsBlockers.length === 0) return null;
          return (
            <>
              <Separator />
              <div className="px-3 py-2 space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">
                  Integridad de HS Code
                </p>
                {hsBlockers.map((b) => {
                  const isMismatch = b.id.startsWith("hs-mismatch::");
                  const Icon = isMismatch ? GitCompareArrows : FileWarning;
                  const docId = b.meta?.documentId;
                  return (
                    <div
                      key={b.id}
                      className={cn(
                        "flex items-start gap-2 text-[11px] rounded-md px-2 py-1.5",
                        b.severity === "critical" ? "bg-destructive/5" : "bg-amber-500/5",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-3.5 w-3.5 shrink-0 mt-0.5",
                          b.severity === "critical" ? "text-destructive" : "text-amber-600 dark:text-amber-400",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "font-medium",
                            b.severity === "critical" ? "text-destructive" : "text-amber-700 dark:text-amber-400",
                          )}
                        >
                          {b.title}
                        </p>
                        <p className="text-muted-foreground leading-snug mt-0.5">{b.message}</p>
                        {docId && onOpenDocument && (
                          <button
                            type="button"
                            onClick={() => {
                              onOpenDocument(docId);
                              setOpen(false);
                            }}
                            className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-semibold text-primary hover:underline"
                          >
                            Abrir Auditoría <ArrowRight className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* Other blockers (cliente, TRM, peso, modo transporte) */}
        {(() => {
          const otherBlockers = blockers.filter(
            (b) =>
              !b.id.startsWith("missing-doc-") &&
              !b.id.startsWith("hs-ia-pending::") &&
              !b.id.startsWith("hs-mismatch::") &&
              !b.id.startsWith("hs-format::"),
          );
          if (otherBlockers.length === 0) return null;
          return (
            <>
              <Separator />
              <div className="px-3 py-2 space-y-1.5">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">
                  Otros pre-requisitos
                </p>
                {otherBlockers.map((b) => (
                  <div key={b.id} className="flex items-start gap-2 text-[11px]">
                    <AlertTriangle
                      className={cn(
                        "h-3 w-3 shrink-0 mt-0.5",
                        b.severity === "critical" ? "text-destructive" : "text-amber-600 dark:text-amber-400",
                      )}
                    />
                    <div className="min-w-0">
                      <p className={cn(
                        "font-medium",
                        b.severity === "critical" ? "text-destructive" : "text-amber-700 dark:text-amber-400",
                      )}>
                        {b.title}
                      </p>
                      <p className="text-muted-foreground leading-snug">{b.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </PopoverContent>
    </Popover>
  );
}
